import TemplateDto from '@/services/skills/Dto/TemplateDto';
import PageTemplateDto from '@/services/skills/Dto/PageTemplateDto';
import TemplateRequestDto from '@/services/skills/Dto/TemplateRequestDto';
import TemplatesApiRepository from '@/services/skills/TemplatesApiRepository';
import { v4 as uuidv4 } from 'uuid';
import { isTemplateInBusinessAccount } from '@/utils/templateBusinessAccount';
import {
    DEFAULT_STEP_KEY,
    defaultSteps,
    templateBelongsToStep,
    templateNameForStep,
} from '@/components/Skills/LixsaSkills/AbandonCart/Utils/AbandonCartSequenceConfig';

const FLOW_TYPE = 'ABANDONED_CHECKOUT';
const ABANDONED_CHECKOUT_SKILL_ID = 'ab10e42f-fb65-48f9-bd0d-28b7489cccbj';
const CATEGORY = 'MARKETING';

const state = () => ({
    templatesList: null,
    currentLanguage: 'en',
    currentTemplate: null,
    /** Secuencia de envíos guardada; null hasta leerla. */
    steps: null,
    /** Si la cuenta tiene cron; null mientras no se sabe (sin leer o la lectura falló). */
    schedulerConfigured: null,
    /** Paso cuyas plantillas se ven y se editan. */
    selectedStepKey: DEFAULT_STEP_KEY,
    availableLanguages: [],
});

const formatLanguage = (language) => {
    if (!language) return '';
    const [base, region] = language.split('_');
    return region ? `${base.toLowerCase()}_${region.toUpperCase()}` : base.toLowerCase();
};

const getters = {
    getCurrentTemplate: (state) => state.currentTemplate,
    getSteps: (state) => state.steps,
    /** Solo `false` es «seguro que no tiene cron»; null no basta para avisar. */
    isSchedulerMissing: (state) => state.schedulerConfigured === false,
    getSelectedStepKey: (state) => state.selectedStepKey,
    /** Plantillas del paso elegido: cada paso tiene una por idioma y línea de WhatsApp. */
    getStepTemplates: (state) =>
        (state.templatesList?.content || []).filter((t) => templateBelongsToStep(t, state.selectedStepKey)),
    getTemplateCountsByStep: (state) =>
        (state.steps || []).reduce((counts, step) => {
            counts[step.key] = (state.templatesList?.content || []).filter((t) =>
                templateBelongsToStep(t, step.key),
            ).length;
            return counts;
        }, {}),
    getCurrentLanguage: (state) => state.currentLanguage,
    getAvailableLanguages: (state) => state.availableLanguages,
    currentTemplateIsSavedInTemplatesList: (state) => {
        if (!state.currentTemplate || !state.templatesList?.content) return false;
        return state.templatesList.content.some((template) => template.uuid === state.currentTemplate.uuid);
    },
    getCurrentTemplateStatus: (state) => {
        if (!state.currentTemplate) return null;

        return state.currentTemplate.status || 'NEW';
    },
};

const mutations = {
    SET_TEMPLATE_LIST(state, templatesList) {
        state.templatesList = templatesList;
    },
    SET_CURRENT_LANGUAGE(state, language) {
        state.currentLanguage = language;
    },
    SET_STEPS(state, steps) {
        state.steps = steps;
    },
    SET_SCHEDULER_CONFIGURED(state, configured) {
        state.schedulerConfigured = configured;
    },
    SET_SELECTED_STEP_KEY(state, key) {
        state.selectedStepKey = key;
    },
    SET_AVAILABLE_LANGUAGES(state, languages) {
        state.availableLanguages = languages;
    },
    SET_CURRENT_TEMPLATE(state, template) {
        state.currentTemplate = template;
    },
    /**
     * Optimistically update a single template's status in the list.
     * Uses splice() to guarantee Vue 2 array reactivity.
     * @param {object} state
     * @param {{ uuid: string, status: string }} payload
     */
    UPDATE_TEMPLATE_STATUS(state, { uuid, status }) {
        if (!state.templatesList?.content) return;
        const idx = state.templatesList.content.findIndex((t) => t.uuid === uuid);
        if (idx !== -1) {
            state.templatesList.content.splice(idx, 1, {
                ...state.templatesList.content[idx],
                status,
            });
        }
    },
};

const actions = {
    async fetchTemplatesList({ commit }) {
        try {
            const templates = await TemplatesApiRepository.getTemplates(FLOW_TYPE);
            commit('SET_TEMPLATE_LIST', templates);
            return templates;
        } catch (error) {
            console.error('Error fetching templates:', error);
            throw error;
        }
    },

    async fetchCurrentTemplate({ commit, state, dispatch }) {
        const foundTemplate = state.templatesList.content.find(
            (template) =>
                templateBelongsToStep(template, state.selectedStepKey) &&
                formatLanguage(template.language) === formatLanguage(state.currentLanguage),
        );
        if (foundTemplate) {
            const existingTemplate = new TemplateDto(foundTemplate);
            commit('SET_CURRENT_TEMPLATE', existingTemplate);
            return;
        }

        try {
            await dispatch('fetchDefaultTemplate');
        } catch (error) {
            console.error('Error fetching current template:', error);
            // throw error; // Optional: don't throw to allow empty state if needed, or keep for error handling
        }
    },

    async fetchDefaultTemplate({ commit, state }) {
        try {
            const templateVariables = await TemplatesApiRepository.getTemplateVariables(
                ABANDONED_CHECKOUT_SKILL_ID,
                FLOW_TYPE,
                state.currentLanguage,
            );
            const builder = new TemplateDto.Builder()
                .withUuid(templateVariables.uuid)
                .withTemplateType(templateVariables.templateType)
                .withFlowType(templateVariables.flowType)
                .withSkillId(templateVariables.skillId)
                .withBody(templateVariables.contentExample)
                .withLanguage(templateVariables.language)
                .withDateCreated(templateVariables.dateCreated);

            // The TemplateDtoBuilder.withVariables method expects an array of objects that look like TemplateVariablesResponseDto
            // BUT it maps them using `new TemplateVariablesResponseDto.Builder()`.
            // Our `templateVariables` is ALREADY a TemplateVariablesResponseDto instance (or close to it).
            // If we pass it as a raw object to withVariables, the builder inside withVariables will try to extract properties.

            // However, looking at TemplateDto.js:
            // withVariables(variables) { this.variables = variables.map(v => new TemplateVariablesResponseDto.Builder()...build()) }

            // If we pass [templateVariables] (which is the DTO), the internal builder will try to read properties from it.
            // Since templateVariables IS the DTO, it has the properties.
            // CAUTION: TemplateVariablesResponseDto CONSTRUCTOR puts properties on `this`.
            // The BUILDER `withVariables` expects input where it can read `.uuid`, `.template_type` (snake case from API usually?) OR builder properties?

            // Wait, TemplateVariablesResponseDto.js uses snake_case for builder properties?
            // checking file... no, it uses camelCase methods like withTemplateType.
            // BUT TemplateDto.js line 139: `.withTemplateType(variable.template_type)`
            // It expects `variable` to have snake_case properties! (which comes from raw API response usually).

            // BUT `templateVariables` here is ALREADY a valid DTO with camelCase properties (uuid, templateType...).
            // So if we pass it to `withVariables`, the mapping  `variable.template_type` will fail (undefined)
            // because our DTO has `templateType`.

            // FIX: We should NOT use the builder's withVariables method if it forces incorrect mapping.
            // Or we must pass an object that matches what the builder expects (snake_case).
            // OR simpler: just assign the array directly to the builder instance property if possible,
            // or bypass the mapping.

            // Since we can't easily change the Builder class in this step without touching another file,
            // let's assign it manually to the built object or use a raw object.

            // OPTION: construct the variables array manually on the builder instance.
            builder.variables = [templateVariables];
            const defaultTemplate = builder.build();

            commit('SET_CURRENT_TEMPLATE', defaultTemplate);
            return defaultTemplate;
        } catch (error) {
            console.error('Error fetching default template:', error);
            throw error;
        }
    },

    async fetchAvailableLanguages({ commit }) {
        try {
            const languages = await TemplatesApiRepository.getAvailableLanguages();
            commit('SET_AVAILABLE_LANGUAGES', languages);
        } catch (error) {
            console.error('Error fetching available languages:', error);
            throw error;
        }
    },

    async fetchCurrentLanguage({ rootGetters, state, commit }) {
        const hasShopifySkill = rootGetters['agentSettings/hasShopifySkill'];
        if (!hasShopifySkill) {
            commit('SET_CURRENT_LANGUAGE', 'en');
            return;
        }

        const shopifySkill = rootGetters['agentSettings/getSkillByInternalName']('Shopify');
        const shopifyLanguage = shopifySkill?.config_values?.countryCode;

        const language = state.availableLanguages.find((language) => language.tag === shopifyLanguage);

        commit('SET_CURRENT_LANGUAGE', language ? language.tag : 'en');
    },

    /**
     * Sin scheduler se ofrecen los pasos base, y `schedulerConfigured` queda en false para que el
     * panel avise si la skill está encendida: sin cron no sale ningún envío.
     */
    async fetchCheckoutScheduler({ commit }) {
        try {
            const { scheduler, configured } =
                await TemplatesApiRepository.getCheckoutScheduler(ABANDONED_CHECKOUT_SKILL_ID);
            commit('SET_STEPS', scheduler.steps?.length ? scheduler.steps : defaultSteps());
            commit('SET_SCHEDULER_CONFIGURED', configured);
        } catch (error) {
            commit('SET_STEPS', defaultSteps());
            // Backends anteriores contestaban 404 sin cron; cualquier otro fallo es «no se sabe».
            commit('SET_SCHEDULER_CONFIGURED', error?.response?.status === 404 ? false : null);
            throw error;
        }
    },

    async updateTemplate(
        { commit, state },
        { templateContent, accountId, businessAccountId, defaultBusinessAccountId },
    ) {
        try {
            // Prefer UUID lookup (via currentTemplate) over language lookup.
            // Language-based find() can fail on format mismatches (e.g. 'en' vs 'en_US')
            // causing an accidental CREATE that duplicates templates in the backend.
            const foundTemplate =
                (state.currentTemplate &&
                    state.templatesList?.content?.find((t) => t.uuid === state.currentTemplate.uuid)) ||
                // Con varias líneas de WhatsApp hay una plantilla por idioma y WABA.
                state.templatesList?.content?.find(
                    (template) =>
                        templateBelongsToStep(template, state.selectedStepKey) &&
                        formatLanguage(template.language) === formatLanguage(state.currentLanguage) &&
                        isTemplateInBusinessAccount(template, businessAccountId, defaultBusinessAccountId),
                );
            if (foundTemplate) {
                // Update template
                const templateRequest = new TemplateRequestDto(foundTemplate);
                templateRequest.body = templateContent;
                await TemplatesApiRepository.updateTemplate(foundTemplate.uuid, templateRequest);
                // Optimistic update: reflect DRAFT immediately so the list view
                // never races against the backend's async status transition.
                // The WebSocket TemplateUpdated event will confirm when it arrives.
                commit('UPDATE_TEMPLATE_STATUS', { uuid: foundTemplate.uuid, status: 'DRAFT' });
            } else {
                // Create template
                const templateRequest = new TemplateRequestDto.Builder()
                    .withUuid(uuidv4())
                    .withAccountId(accountId)
                    .withBody(templateContent)
                    .withLanguage(formatLanguage(state.currentLanguage))
                    .withTemplateName(templateNameForStep(state.selectedStepKey))
                    .withCategory(CATEGORY)
                    .withType('TEXT')
                    .withTemplateType('FLOW')
                    .withFlowType(FLOW_TYPE)
                    .withSkillId(ABANDONED_CHECKOUT_SKILL_ID)
                    .withChannelType('WHATSAPP')
                    .withBusinessAccountId(businessAccountId)
                    .build();
                await TemplatesApiRepository.createTemplate(templateRequest);
            }
        } catch (error) {
            console.error('Error updating template:', error);
            throw error;
        }
    },
    async deleteTemplate({ commit, state }, templateUuid) {
        try {
            const foundTemplate = state.templatesList.content.find((template) => template.uuid === templateUuid);
            if (foundTemplate) {
                const templateRequest = new TemplateRequestDto(foundTemplate);
                await TemplatesApiRepository.deleteTemplate(templateUuid, templateRequest);

                // Optimistic UI update: Remove from local list immediately
                const updatedContent = state.templatesList.content.filter((t) => t.uuid !== templateUuid);
                // We need to construct a new PageTemplateDto or object shaped like it.
                // Assuming PageTemplateDto constructor or Builder handles the structure.
                // Since state.templatesList is likely a PageTemplateDto instance or plain object from API response.
                // Looking at fetchTemplatesList, it does: return new PageTemplateDto...

                // Let's assume we can spread current properties.
                const updatedList = new PageTemplateDto.Builder()
                    .withTotalElements(state.templatesList.totalElements - 1)
                    .withTotalPages(state.templatesList.totalPages)
                    .withFirst(state.templatesList.first)
                    .withLast(state.templatesList.last)
                    .withNumberOfElements(updatedContent.length)
                    .withSize(state.templatesList.size)
                    .withContent(updatedContent)
                    .withNumber(state.templatesList.number)
                    .withEmpty(updatedContent.length === 0)
                    .build();

                commit('SET_TEMPLATE_LIST', updatedList);
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            throw error;
        }
    },
    setCurrentLanguage({ commit }, language) {
        commit('SET_CURRENT_LANGUAGE', formatLanguage(language));
    },
    setSelectedStepKey({ commit }, key) {
        commit('SET_SELECTED_STEP_KEY', key);
    },
    /** @param {CheckoutSchedulerDto} scheduler Con la secuencia en `steps`. */
    async saveCheckoutScheduler({ commit }, scheduler) {
        try {
            scheduler.skill_id = ABANDONED_CHECKOUT_SKILL_ID;
            await TemplatesApiRepository.createCheckoutScheduler(ABANDONED_CHECKOUT_SKILL_ID, scheduler);
            commit('SET_STEPS', scheduler.steps);
            commit('SET_SCHEDULER_CONFIGURED', true);
        } catch (error) {
            console.error('Error creating checkout scheduler:', error);
            throw error;
        }
    },

    async subscribeToTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        try {
            await dispatch(
                'websocket/subscribe',
                { topic, callback: (data) => dispatch('handleTemplateEvent', data) },
                { root: true },
            );
        } catch (error) {
            console.error(`Failed to subscribe to ${topic}:`, error);
        }
    },

    unsubscribeFromTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        dispatch('websocket/unsubscribe', { topic }, { root: true });
    },

    handleTemplateEvent({ commit, state, dispatch }, event) {
        if (!event || !event.data) return;

        // Parse JSON string if it's a string
        const templateData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        // Create template using builder pattern to ensure proper object construction
        const template = new TemplateDto.Builder()
            .withUuid(templateData.uuid)
            .withAccountId(templateData.accountId)
            .withBody(templateData.body)
            .withHeader(templateData.header)
            .withChannelType(templateData.channelType)
            .withTemplateName(templateData.templateName)
            .withLanguage(templateData.language)
            .withCategory(templateData.category)
            .withStatus(templateData.status)
            .withScore(templateData.score)
            .withType(templateData.type)
            .withTemplateType(templateData.templateType)
            .withFlowType(templateData.flowType)
            .withSkillId(templateData.skillId)
            .withInternalTemplateId(templateData.internalTemplateId)
            .withTemplateParameters(templateData.templateParameters)
            .withExtraParameters(templateData.extraParameters)
            .withIssues(templateData.issues)
            .withModifyBy(templateData.modifyBy)
            .withDateCreated(templateData.dateCreated)
            .withVariables(templateData.variables || [])
            .build();

        const updatedTemplatesList = new PageTemplateDto(state.templatesList);
        const templateIndex = updatedTemplatesList.content.findIndex((t) => t.uuid === template.uuid);
        switch (event.name) {
            case 'TemplateUpdated':
                // Find and update the template in the list
                if (templateIndex !== -1) {
                    updatedTemplatesList.content[templateIndex] = template;
                    commit('SET_TEMPLATE_LIST', updatedTemplatesList);

                    // If current template is the updated one, fetch it again
                    if (state.currentTemplate?.uuid === template.uuid) {
                        dispatch('fetchCurrentTemplate');
                    }
                }
                break;
            default:
                console.warn(`Unhandled template event: ${event.name}`);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
